import { AlertTriangle, ShieldCheck, UserCheck } from 'lucide-react'
import { useEffect, useId, useState, type ReactNode } from 'react'

import type { PendingApproval } from '@/api/approvals-types'
import type { Branch } from '@/api/types'
import { ProblemAlert } from '@/components/error-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatDateTime } from '@/lib/datetime'
import type { ApiError } from '@/lib/problem-details'
import { cn } from '@/lib/utils'

import { ApprovalAmount } from './approval-presentation'
import { branchDisplayName } from './branch-display'
import { describeExpiry } from './expiry'
import { IRREVERSIBLE_CONFIRMATION, describeRequestType } from './request-types'

/**
 * The one place a decision is actually taken.
 *
 * Three things are true of both decisions and are stated on the dialog rather than assumed:
 *
 * **Approving is not filing a form.** The countersignature re-dispatches the maker's stored
 * command through the whole pipeline — validation, authorisation, transaction — against current
 * balances, current periods and current account states. The figure below was authorised when it
 * was submitted; what executes is checked now.
 *
 * **Both identities are recorded.** The maker is on the request, the checker is the person
 * pressing this button, and the trail names both. Neither can be the same person: the engine
 * refuses it in the aggregate as well as in the service.
 *
 * **A decision is not undoable from here.** There is no un-approve and no un-reject endpoint. A
 * rejected request is finished and the maker must submit the operation again; an approved one has
 * already run.
 */

/**
 * What to do next, for the refusals this dialog produces.
 *
 * The shared error table in lib/error-messages.ts says what went wrong for the codes it knows.
 * These say what a checker does about it, which is specific to standing in front of a queue — and
 * they are kept here rather than added to the shared table because they are advice for one screen.
 */
const DECISION_HINTS: Record<string, string> = {
  'access.self_approval_forbidden':
    'The queue excludes your own requests, so seeing this means the request was raised under your account by another route. It needs a different checker.',
  'access.approval_permission_missing':
    'Your role no longer holds the countersigning permission for this operation — it may have been changed since this screen loaded. Reload, and ask an administrator if the row is still here.',
  'access.approval_branch_mismatch':
    'This request belongs to a branch you cannot act for. An institution-wide request needs a checker with cross-branch reach.',
  'access.approval_limit_exceeded':
    'The amount is above your approval ceiling for this operation. Somebody with a higher delegated authority has to countersign it — approval limits are configuration, not something this screen can see in advance.',
  'access.maker_no_longer_active':
    'The person who submitted this has been disabled or removed since. Nothing can execute on their authority now: the operation has to be raised again by someone active.',
  'access.approval_expired':
    'It lapsed before a decision was taken. It has left the queue; the maker submits the operation again.',
  'access.approval_not_pending':
    'Somebody else has already decided this one. Refresh the queue — the row you are looking at is stale.',
  'access.approval_payload_tampered':
    'The stored request no longer matches its hash, so the engine will not run it. This is a security event: report it before anything else.',
  'access.approval_command_unknown':
    'The stored command is not present in this build of the server, which usually means the request outlived a deployment that removed it. It cannot be executed and has to be raised again.',
}

/** Long enough to be a sentence somebody can act on. The server's own floor for a reason is five. */
const MINIMUM_TEXT_LENGTH = 5

/** The server truncates nothing — it refuses. Both fields are capped at a thousand characters. */
const MAXIMUM_TEXT_LENGTH = 1000

export type DecisionMode = 'approve' | 'reject'

export interface DecisionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: DecisionMode
  approval: PendingApproval
  branches: Branch[]
  isPending: boolean
  error: ApiError | null
  onConfirm: (text: string) => void
}

export function DecisionDialog({
  open,
  onOpenChange,
  mode,
  approval,
  branches,
  isPending,
  error,
  onConfirm,
}: DecisionDialogProps) {
  const textId = useId()
  const phraseId = useId()

  const [text, setText] = useState('')
  const [phrase, setPhrase] = useState('')

  useEffect(() => {
    if (!open) {
      setText('')
      setPhrase('')
    }
  }, [open])

  const descriptor = describeRequestType(approval.requestType)
  const expiry = describeExpiry(approval.expiresAtUtc)

  // The typed word is asked for only where countersigning moves money out or forgives a debt, and
  // only on approval. A rejection changes nothing by itself, so gating it the same way would
  // train people to type the word without reading it.
  const requiresPhrase = mode === 'approve' && descriptor.isIrreversible

  const trimmed = text.trim()
  const textSatisfied = trimmed.length >= MINIMUM_TEXT_LENGTH && trimmed.length <= MAXIMUM_TEXT_LENGTH
  const phraseSatisfied = !requiresPhrase || phrase.trim() === IRREVERSIBLE_CONFIRMATION
  const canConfirm = textSatisfied && phraseSatisfied && !isPending

  const confirm = () => {
    if (canConfirm) {
      onConfirm(trimmed)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'approve' ? 'Countersign this request' : 'Refuse this request'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'approve'
              ? 'Approving runs the command the maker submitted, now, against current balances and the current accounting period.'
              : 'Refusing ends the request. The maker has to submit the operation again if it is still wanted.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <div className="rounded-card border border-border-subtle bg-surface-muted p-3">
            <p className="text-sm font-medium text-heading">{approval.description}</p>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
              <Summary label="Operation" value={descriptor.label} />
              <Summary label="Submitted by" value={approval.makerUsername} />
              <Summary
                label="Branch"
                value={branchDisplayName(approval.branchId, branches)}
              />
              <Summary label="Amount" value={<ApprovalAmount approval={approval} />} />
              <Summary
                label="Lapses"
                value={`${formatDateTime(approval.expiresAtUtc)} · ${expiry.label}`}
              />
              <Summary label="Request" value={approval.requestId} mono />
            </dl>
          </div>

          {mode === 'approve' && descriptor.isIrreversible ? (
            <Alert tone="danger">
              <AlertTriangle aria-hidden />
              <div className="space-y-1">
                <AlertTitle>This one moves money or forgives a debt</AlertTitle>
                <AlertDescription>
                  Countersigning a {descriptor.label.toLowerCase()} is not reversible by another
                  click. If it turns out to be wrong, putting it right is a fresh operation of its
                  own — a reversal, a re-lodgement — with its own countersignature and its own
                  entry in the trail.
                </AlertDescription>
              </div>
            </Alert>
          ) : null}

          {mode === 'approve' && approval.amountBasis === 'unreadable' ? (
            <Alert tone="danger">
              <AlertTriangle aria-hidden />
              <div className="space-y-1">
                <AlertTitle>The amount on this request could not be read</AlertTitle>
                <AlertDescription>
                  The server sent something this screen cannot read as money, so no figure is being
                  shown to you. The stored command still carries its own amount and the engine will
                  execute it. Find out what it is before countersigning.
                </AlertDescription>
              </div>
            </Alert>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor={textId}>
              {mode === 'approve' ? 'Note (recorded against your name)' : 'Reason for refusal'}
            </Label>
            <textarea
              id={textId}
              value={text}
              rows={3}
              autoFocus
              maxLength={MAXIMUM_TEXT_LENGTH}
              onChange={(event) => setText(event.target.value)}
              className={cn(
                'w-full rounded-control border border-border-subtle bg-surface px-3 py-2 text-sm text-heading transition-colors',
                'placeholder:text-muted hover:border-border-strong focus:border-brand-text focus:outline-none',
              )}
              placeholder={
                mode === 'approve'
                  ? 'What you checked before agreeing to this.'
                  : 'What the maker has to change, or why this cannot go through.'
              }
            />
            <p className="text-2xs text-muted">
              {mode === 'approve' ? (
                <>
                  The API accepts an empty note; this screen does not. Your countersignature is the
                  second half of a control, and the trail is the only place it is ever explained.
                </>
              ) : (
                <>
                  Mandatory, and the only thing the maker is told. In a group-lending branch a
                  refused disbursement means somebody leaves the meeting empty-handed — they need
                  something to say.
                </>
              )}{' '}
              {String(trimmed.length)}/{String(MAXIMUM_TEXT_LENGTH)} characters.
            </p>
          </div>

          {requiresPhrase ? (
            <div className="space-y-1.5">
              <Label htmlFor={phraseId}>
                Type <span className="font-mono font-semibold">{IRREVERSIBLE_CONFIRMATION}</span> to
                unlock
              </Label>
              <Input
                id={phraseId}
                value={phrase}
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setPhrase(event.target.value)}
              />
            </div>
          ) : null}

          {error ? (
            <div className="space-y-2">
              <ProblemAlert error={error} />
              {DECISION_HINTS[error.code] ? (
                <p className="text-xs text-body">{DECISION_HINTS[error.code]}</p>
              ) : null}
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant={mode === 'approve' ? 'primary' : 'danger'}
            onClick={confirm}
            disabled={!canConfirm}
          >
            {isPending ? (
              'Working…'
            ) : mode === 'approve' ? (
              <>
                <ShieldCheck />
                Countersign
              </>
            ) : (
              <>
                <UserCheck />
                Refuse
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Summary({
  label,
  value,
  mono = false,
}: {
  label: string
  value: ReactNode
  mono?: boolean
}) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className={cn('mt-0.5 text-sm break-words text-heading', mono && 'font-mono text-xs')}>
        {value}
      </dd>
    </div>
  )
}
